import { useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';

interface LiquidGlassProps {
  children: ReactNode;
  className?: string;
  /** width of the curved edge (px) where refraction happens */
  bezel?: number;
  /** max pixel offset of the refraction */
  strength?: number;
}

/* Chromium is the only engine that renders SVG filters inside backdrop-filter */
const supportsSvgBackdrop = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isChromium = /Chrome\/|Chromium\//.test(ua) && !/CriOS|FxiOS|Firefox/.test(ua);
  return isChromium && typeof CSS !== 'undefined' && CSS.supports('backdrop-filter', 'url(#a)');
};

/* displacement map: neutral (128,128) interior, inward-pointing vectors inside the bezel */
const buildMap = (w: number, h: number, bezel: number): string => {
  const k = 0.5;
  const cw = Math.max(2, Math.round(w * k));
  const ch = Math.max(2, Math.round(h * k));
  const b = Math.max(2, bezel * k);
  const canvas = document.createElement('canvas');
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  const img = ctx.createImageData(cw, ch);
  const profile = (d: number) => {
    if (d >= b) return 0;
    const t = 1 - d / b;
    return t * t * (3 - 2 * t) * t; // smooth, steepest at the rim
  };
  const bx = Math.max(2, b * 0.5);
  const profileX = (d: number) => {
    if (d >= bx) return 0;
    const t = 1 - d / bx;
    return t * t * t * 0.5; // left/right edges: much narrower and weaker
  };
  for (let y = 0; y < ch; y += 1) {
    const dy = profile(y) - profile(ch - 1 - y);
    for (let x = 0; x < cw; x += 1) {
      const dx = profileX(x) - profileX(cw - 1 - x);
      const i = (y * cw + x) * 4;
      img.data[i] = 128 + dx * 127;
      img.data[i + 1] = 128 + dy * 127;
      img.data[i + 2] = 128;
      img.data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  return canvas.toDataURL('image/png');
};

export default function LiquidGlass({ children, className = '', bezel = 6, strength = 8 }: LiquidGlassProps) {
  const ref = useRef<HTMLDivElement>(null);
  const rawId = useId();
  const filterId = `lg-${rawId.replace(/[^a-zA-Z0-9]/g, '')}`;
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [map, setMap] = useState('');
  const [optical, setOptical] = useState(false);

  useEffect(() => {
    setOptical(supportsSvgBackdrop());
    const el = ref.current;
    if (!el) return undefined;
    let t: number | undefined;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setSize({ w: Math.round(r.width), h: Math.round(r.height) });
    };
    measure();
    const ro = new ResizeObserver(() => {
      window.clearTimeout(t);
      t = window.setTimeout(measure, 120);
    });
    ro.observe(el);
    return () => { ro.disconnect(); window.clearTimeout(t); };
  }, []);

  useEffect(() => {
    if (!optical || !size.w || !size.h) return;
    setMap(buildMap(size.w, size.h, bezel));
  }, [optical, size.w, size.h, bezel]);

  const active = optical && !!map;
  /* tinted copy: luminance → 1 - darkness*tint; fringes take the tint's hue (palette-matched instead of RGB) */
  const tintM = (r: number, g: number, b: number) => {
    const row = (t: number) => `${(t * 0.333).toFixed(3)} ${(t * 0.333).toFixed(3)} ${(t * 0.334).toFixed(3)} 0 ${(1 - t).toFixed(3)}`;
    return `${row(r)}  ${row(g)}  ${row(b)}  0 0 0 1 0`;
  };

  return (
    <div ref={ref} className={`lg-clear ${className}`}>
      {active && (
        <svg width="0" height="0" aria-hidden="true" className="lg-defs">
          <filter
            id={filterId}
            x="0"
            y="0"
            width={size.w}
            height={size.h}
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feImage href={map} x="0" y="0" width={size.w} height={size.h} preserveAspectRatio="none" result="map" />
            {/* low-frequency liquid field: each colour channel drifts a different amount → soft natural iridescence */}
            <feTurbulence type="fractalNoise" baseFrequency="0.0035 0.006" numOctaves="2" seed="7" result="noise" />
            <feDisplacementMap in="SourceGraphic" in2="map" scale={strength} xChannelSelector="R" yChannelSelector="G" result="edge" />
            <feDisplacementMap in="edge" in2="noise" scale="22" xChannelSelector="R" yChannelSelector="G" result="dR" />
            <feDisplacementMap in="edge" in2="noise" scale="8" xChannelSelector="G" yChannelSelector="R" result="dG" />
            <feDisplacementMap in="edge" in2="noise" scale="-14" xChannelSelector="R" yChannelSelector="G" result="dB" />
            {/* periwinkle / rose-lilac / silver fringes */}
            <feColorMatrix in="dR" type="matrix" values={tintM(0.42, 0.4, 0.12)} result="r" />
            <feColorMatrix in="dG" type="matrix" values={tintM(0.18, 0.42, 0.14)} result="g" />
            <feColorMatrix in="dB" type="matrix" values={tintM(0.3, 0.3, 0.26)} result="b" />
            <feBlend in="r" in2="g" mode="multiply" result="rg" />
            <feBlend in="rg" in2="b" mode="multiply" />
          </filter>
        </svg>
      )}
      {/* optical layer: refracts whatever is behind it */}
      <div
        className="lg-optic"
        aria-hidden="true"
        style={active
          ? { backdropFilter: `url(#${filterId}) blur(7px) saturate(1.7) brightness(1.04)`, WebkitBackdropFilter: `url(#${filterId}) blur(7px) saturate(1.7) brightness(1.04)` }
          : { backdropFilter: 'blur(7px) saturate(1.7) brightness(1.04)', WebkitBackdropFilter: 'blur(7px) saturate(1.7) brightness(1.04)' }}
      />
      {/* rim + specular highlights */}
      <div className="lg-rim" aria-hidden="true" />
      {/* foreground content — never filtered */}
      <div className="lg-content">{children}</div>
    </div>
  );
}